import { AxiosError } from 'axios';

import { FieldMappingEditorValue } from '@components/text-editors/addons/field-mapping';

import { unhandledRequest } from 'api/request';

import { FieldConfigValue, JourneyAttribute, JourneyAttributeType } from './field-config/types';

export async function getToken() {
  const response = await unhandledRequest.get<{ ok: boolean; valid: boolean }>({
    url: `/integrations/journeys/settings/token/`,
  });
  return response.data;
}

export type ConnectType = {
  clientId: string;
  clientSecret: string;
};
export type ConnectError = AxiosError<{
  ok: boolean;
  error: string;
}>;

export async function connect(data: ConnectType) {
  const response = await unhandledRequest.post({
    url: `/integrations/journeys/settings/token/`,
    data,
  });
  return response.data;
}

export async function disconnect() {
  const response = await unhandledRequest.delete({
    url: `/integrations/journeys/settings/token/`,
  });
  return response.data;
}

export type ApplicantJourneyType = {
  id: string;
  journeyId: string;
  journeyTitle: string;
  journeyDescription: string;
  dateCreated: string;
  status: JourneyStatus;
  numActions: number;
  numCompleted: number;
  numHalted: number;
  numInProgress: number;
  numToBeStarted: number;
  workspaceId: string;
};

export type JourneyStatus = 'completed' | 'in_progress' | 'paused' | 'not_started';

export async function applicationJourneys(applicationId: number) {
  const response = await unhandledRequest.get<ApplicantJourneyType[]>({
    url: `/integrations/journeys/application/${applicationId}/journeys/`,
  });
  return response.data;
}

export type JourneyType = {
  shortId: string;
  description: string;
  title: string;
  hasPublishedJourney: boolean;
};

export async function journeys() {
  const response = await unhandledRequest.get<JourneyType[]>({
    url: `/integrations/journeys/list/`,
  });
  return response.data;
}

export type JourneyStatsType = {
  shortId: string;
  numNodeActions: number;
  numTravellers: number;
};

export async function statistics() {
  const response = await unhandledRequest.get<JourneyStatsType[]>({
    url: `/integrations/journeys/statistics/`,
  });
  return response.data;
}

export type AttributeType = JourneyAttribute;
export async function companyTravellerAttributes() {
  const response = await unhandledRequest.get<AttributeType[]>({
    url: `/integrations/journeys/company-traveller-attributes/`,
  });
  return response.data;
}

/**
 * One declaration, one domain concept. The previous copy of this union was
 * missing `number` and `decimal`, which is why the send sidebar silently
 * rendered nothing for them.
 */
export type AttributeTemplateType = JourneyAttributeType;

export async function journeyTravellerAttributes(journeyId: string) {
  const response = await unhandledRequest.get<AttributeType[]>({
    url: `/integrations/journeys/journey-traveller-attributes/${journeyId}/`,
  });
  return response.data;
}

export type UserType = {
  shortId: string;
  name: string;
};
export async function users() {
  const response = await unhandledRequest.get<UserType[]>({
    url: `/integrations/journeys/users/`,
  });
  return response.data;
}

type Option = {
  shortId: string;
  value: string;
  label: string;
};
export type ListType = {
  shortId: string;
  name: string;
  options: Option[];
};
export async function list(listId: string) {
  const response = await unhandledRequest.get<ListType>({
    url: `/integrations/journeys/list/${listId}/`,
  });
  return response.data;
}

export type AddToJourneyType = {
  journey: string;
  applications: number[];
  name: FieldMappingEditorValue | string;
  email: FieldMappingEditorValue | string;
  phone: FieldMappingEditorValue | string;
  startJourney: boolean;
  attributes: { [key: string]: FieldConfigValue };
};
export async function addToJourney(data: AddToJourneyType) {
  const response = await unhandledRequest.post({
    url: `/integrations/journeys/add-to-journey/`,
    data,
  });
  return response.data;
}

export async function removeFromJourney(applicationId: number, travellerJourneyId: string) {
  const response = await unhandledRequest.delete({
    url: `/integrations/journeys/application/${applicationId}/remove-journey/${travellerJourneyId}/`,
  });
  return response.data;
}
